import { Animated, NativeScrollEvent, NativeSyntheticEvent } from 'react-native'
import { IOScrollView } from '@components/IntersectionObserver'
import { View, isWeb, useMedia } from 'tamagui'
import { useThemeColor } from '@hooks/useThemeColor'
import { useScrollBar } from './useScrollBar'

interface IScrollBarProps {
  children: React.ReactNode
  maxHeight: number
  displayNativeIndicator?: boolean
  scrollBarRight?: number
  scrollBarId: string
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void
  /**
   * Faz a área de scroll ocupar toda a largura disponível. Sem isso o
   * IOScrollView fica sem `flex` dentro do container `flexDirection: row` e a
   * largura vira shrink-to-fit do conteúdo — então filhos com `width: 100%`
   * não esticam (medido: 692px em vez de 1101px). Opt-in para não alterar os
   * callers que já dependem do dimensionamento por conteúdo.
   */
  fillContentWidth?: boolean
  /** Mantém a largura do conteúdo quando a barra aparece ou desaparece. */
  reserveScrollBarSpace?: boolean
}

export function ScrollBar({
  children,
  maxHeight,
  displayNativeIndicator = true,
  scrollBarRight = 0,
  scrollBarId,
  onScroll,
  fillContentWidth = false,
  reserveScrollBarSpace = false,
}: IScrollBarProps) {
  const media = useMedia()
  const scrollBarThumbColor = useThemeColor('scrollbar-thumb')
  const {
    displayScrollBar,
    scrollPosition,
    yPosition,
    isInteracting,
    scrollViewRef,
    trackRef,
    handleScroll,
    handleScrollStart,
    handleScrollEnd,
    handleMouseDown,
    handleLayout,
    handleContentSizeChange,
    isScrollingContent,
  } = useScrollBar({
    maxHeight,
    scrollBarId,
    onScroll,
  })

  const scrollBarMarginLeft = media.gtSm ? 14 : 10

  return (
    <View style={{ flex: 1, flexDirection: 'row' }} maxHeight={maxHeight}>
      <IOScrollView
        ref={scrollViewRef}
        style={fillContentWidth ? { flex: 1 } : undefined}
        showsVerticalScrollIndicator={displayNativeIndicator}
        bounces={false}
        onLayout={handleLayout}
        onContentSizeChange={handleContentSizeChange}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        nestedScrollEnabled={!isScrollingContent}
        onScrollBeginDrag={handleScrollStart}
        onScrollEndDrag={handleScrollEnd}
        onMomentumScrollEnd={handleScrollEnd}
      >
        {children}
      </IOScrollView>
      {(displayScrollBar || reserveScrollBarSpace) && (
        <View
          ref={trackRef}
          backgroundColor="$progress-track"
          onMouseDown={isWeb && displayScrollBar ? handleMouseDown : undefined}
          pointerEvents={displayScrollBar ? 'auto' : 'none'}
          style={{
            opacity: displayScrollBar ? 1 : 0,
            height: '100%',
            width: media.gtSm ? 8 : 4,
            borderRadius: 8,
            marginLeft: scrollBarMarginLeft,
            right: scrollBarRight,
            position: 'relative',
            ...(isWeb && {
              cursor: 'pointer',
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }),
          }}
        >
          <Animated.View
            style={{
              width: media.gtSm ? 8 : 4,
              borderRadius: 8,
              backgroundColor: scrollBarThumbColor,
              height: `${scrollPosition}%`,
              transform: [{ translateY: yPosition }],
              minHeight: `${scrollPosition}%`,
              maxHeight,
              opacity: isInteracting ? 0.8 : 1,
            }}
          />
        </View>
      )}
    </View>
  )
}
